import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, Copy, Check, Smartphone, Monitor, RefreshCw, Loader2, ExternalLink, ShieldCheck, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogTrigger } from './ui/dialog';
import { API_URL } from '../utils/api';

/**
 * Apple Calendar (and Google Calendar) subscription modal.
 * Trigger renders inline (admin sidebar). Click → opens modal with the live ICS URL,
 * copy button, webcal:// auto-open link, and platform setup steps.
 */
export function CalendarSyncButton() {
  const [open, setOpen] = useState(false);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [error, setError] = useState(null);

  const fetchToken = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.post(`${API_URL}/api/admin/calendar/sync-url`, {}, { withCredentials: true });
      if (r?.data?.token) {
        setToken(r.data.token);
      } else {
        setError('Server returned an empty response. Please retry.');
      }
    } catch (e) {
      const status = e?.response?.status;
      let msg = e?.message || 'Network error';
      if (status === 401) msg = 'You are signed out. Please log in again as admin.';
      else if (status === 403) msg = 'Admin access required.';
      else if (status >= 500) msg = `Server error (${status}). Please try again in a moment.`;
      else if (status === 404) msg = 'Calendar sync endpoint not found. Server may need redeployment.';
      else if (e?.response?.data?.detail) msg = e.response.data.detail;
      setError(msg);
      console.error('Calendar sync URL fetch failed:', e);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (open && !token) fetchToken();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const httpsUrl = token ? `${window.location.origin}/api/calendar/admin/${token}.ics` : '';
  const webcalUrl = token ? `webcal://${window.location.host}/api/calendar/admin/${token}.ics` : '';

  const copyUrl = async () => {
    if (!httpsUrl) return;
    try {
      await navigator.clipboard.writeText(httpsUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  const rotate = async () => {
    if (!window.confirm('Rotate token? Your existing Apple Calendar subscription will stop syncing — you will need to re-subscribe with the new URL.')) return;
    setRotating(true);
    setError(null);
    try {
      const r = await axios.post(`${API_URL}/api/admin/calendar/rotate-token`, {}, { withCredentials: true });
      setToken(r.data.token);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to rotate token');
      console.error(e);
    }
    setRotating(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid="calendar-sync-button"
          className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-[12px] font-semibold border border-[#1A1A1A] hover:border-[#16A34A]/40 hover:bg-[#16A34A]/5 hover:text-[#16A34A] text-neutral-300 transition-all">
          <Calendar className="h-3.5 w-3.5" />
          Apple Calendar Sync
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#0F0F0F] border-[#1A1A1A] max-w-lg p-0 overflow-hidden" data-testid="calendar-sync-dialog">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-[#1A1A1A]">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-[#16A34A]/10 border border-[#16A34A]/20 flex items-center justify-center flex-shrink-0">
              <Calendar className="h-5 w-5 text-[#16A34A]" />
            </div>
            <div>
              <h2 className="text-lg font-bold font-heading">Apple Calendar Sync</h2>
              <p className="text-[11px] text-neutral-500 font-body">Live feed of every booking — online & offline</p>
            </div>
          </div>
        </div>

        <div className="px-6 py-5 max-h-[70vh] overflow-y-auto">
          {loading ? (
            <div className="py-10 text-center" data-testid="calendar-loading">
              <Loader2 className="h-6 w-6 animate-spin mx-auto text-[#16A34A]" />
              <p className="mt-3 text-[11px] text-neutral-500 font-body">Fetching your subscription URL…</p>
            </div>
          ) : error ? (
            <div className="py-6" data-testid="calendar-error">
              <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4 mb-4 flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="text-[13px] font-semibold text-red-400 mb-1">Couldn't load the URL</div>
                  <div className="text-[12px] text-neutral-300 font-body leading-relaxed break-words">{error}</div>
                </div>
              </div>
              <button
                onClick={fetchToken}
                data-testid="calendar-retry"
                className="w-full h-10 inline-flex items-center justify-center gap-2 bg-[#16A34A] hover:bg-[#15803D] text-white text-[13px] font-semibold rounded-lg transition-colors"
              >
                <RefreshCw className="h-4 w-4" /> Retry
              </button>
              <p className="mt-3 text-[10px] text-neutral-600 font-body text-center">
                Still stuck? Open browser DevTools (F12) → Console tab → share any red errors with support.
              </p>
            </div>
          ) : !token ? (
            <div className="py-6 text-center" data-testid="calendar-empty">
              <p className="text-[12px] text-neutral-500 font-body mb-3">No subscription URL available.</p>
              <button
                onClick={fetchToken}
                className="h-10 px-4 inline-flex items-center gap-2 bg-[#16A34A] hover:bg-[#15803D] text-white text-[13px] font-semibold rounded-lg transition-colors"
              >
                <RefreshCw className="h-4 w-4" /> Generate URL
              </button>
            </div>
          ) : (
            <>
              {/* The URL */}
              <div className="mb-5">
                <label className="block text-[10px] text-neutral-500 uppercase tracking-wider font-body mb-2">Subscription URL</label>
                <div className="flex gap-2">
                  <input
                    readOnly
                    value={httpsUrl}
                    className="flex-1 text-[11px] font-mono bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg px-3 h-10 outline-none focus:border-[#16A34A]"
                    style={{ padding: '0 0.75rem' }}
                    data-testid="calendar-url-input"
                    onClick={(e) => e.target.select()}
                  />
                  <button
                    onClick={copyUrl}
                    data-testid="copy-calendar-url"
                    className={`h-10 px-3 rounded-lg flex items-center gap-1.5 text-[12px] font-semibold transition-colors ${copied ? 'bg-[#16A34A] text-white' : 'bg-[#171717] border border-[#1A1A1A] hover:border-[#16A34A]/40 text-neutral-300'}`}
                  >
                    {copied ? <><Check className="h-3.5 w-3.5" /> Copied</> : <><Copy className="h-3.5 w-3.5" /> Copy</>}
                  </button>
                </div>
                <a
                  href={webcalUrl}
                  className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-[#16A34A] hover:underline font-body"
                  data-testid="webcal-link"
                >
                  <ExternalLink className="h-3 w-3" /> One-click subscribe (Mac · opens Calendar app)
                </a>
              </div>

              {/* Steps */}
              <div className="space-y-3 mb-4">
                {/* Mac */}
                <div className="bg-[#111] border border-[#1A1A1A] rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2.5">
                    <Monitor className="h-3.5 w-3.5 text-[#16A34A]" />
                    <span className="text-[12px] font-semibold font-heading">On Mac (Calendar app)</span>
                  </div>
                  <ol className="space-y-1.5 text-[12px] text-neutral-400 font-body leading-relaxed">
                    <li><span className="text-neutral-600">1.</span> Open <span className="text-white">Calendar</span> → menu <span className="text-white">File</span> → <span className="text-white">New Calendar Subscription…</span></li>
                    <li><span className="text-neutral-600">2.</span> Paste the URL above → click <span className="text-white">Subscribe</span></li>
                    <li><span className="text-neutral-600">3.</span> Set <span className="text-white">Auto-refresh</span> to <span className="text-[#16A34A]">Every 15 minutes</span> → <span className="text-white">OK</span></li>
                  </ol>
                </div>

                {/* iPhone */}
                <div className="bg-[#111] border border-[#1A1A1A] rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2.5">
                    <Smartphone className="h-3.5 w-3.5 text-[#16A34A]" />
                    <span className="text-[12px] font-semibold font-heading">On iPhone / iPad</span>
                  </div>
                  <ol className="space-y-1.5 text-[12px] text-neutral-400 font-body leading-relaxed">
                    <li><span className="text-neutral-600">1.</span> Open <span className="text-white">Settings</span> → <span className="text-white">Calendar</span> → <span className="text-white">Accounts</span> → <span className="text-white">Add Account</span></li>
                    <li><span className="text-neutral-600">2.</span> Choose <span className="text-white">Other</span> → <span className="text-white">Add Subscribed Calendar</span></li>
                    <li><span className="text-neutral-600">3.</span> Paste the URL → <span className="text-white">Next</span> → <span className="text-white">Save</span></li>
                  </ol>
                </div>

                {/* Google */}
                <details className="bg-[#111] border border-[#1A1A1A] rounded-xl p-4 group">
                  <summary className="flex items-center justify-between cursor-pointer list-none">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-3.5 w-3.5 text-[#16A34A]" />
                      <span className="text-[12px] font-semibold font-heading">Google Calendar (optional)</span>
                    </div>
                    <span className="text-[10px] text-neutral-600">Show steps</span>
                  </summary>
                  <ol className="mt-3 space-y-1.5 text-[12px] text-neutral-400 font-body leading-relaxed">
                    <li><span className="text-neutral-600">1.</span> Open <a href="https://calendar.google.com" target="_blank" rel="noopener noreferrer" className="text-[#16A34A] hover:underline">calendar.google.com</a> on a desktop</li>
                    <li><span className="text-neutral-600">2.</span> Sidebar → <span className="text-white">Other calendars</span> → <span className="text-white">+</span> → <span className="text-white">From URL</span></li>
                    <li><span className="text-neutral-600">3.</span> Paste the URL → <span className="text-white">Add calendar</span></li>
                  </ol>
                </details>
              </div>

              {/* Info + Rotate */}
              <div className="bg-[#16A34A]/5 border border-[#16A34A]/20 rounded-xl p-3.5 mb-3 flex items-start gap-2.5">
                <ShieldCheck className="h-3.5 w-3.5 text-[#16A34A] flex-shrink-0 mt-0.5" />
                <div className="text-[11px] text-neutral-300 font-body leading-relaxed">
                  This is a private link. Anyone with it can read your bookings. Don't share it. If it leaks, click <span className="text-[#16A34A] font-semibold">Rotate</span> to invalidate and replace.
                </div>
              </div>

              <div className="flex items-center justify-between gap-2">
                <button
                  onClick={rotate}
                  disabled={rotating}
                  data-testid="rotate-token-button"
                  className="text-[11px] text-neutral-500 hover:text-red-400 inline-flex items-center gap-1.5 transition-colors disabled:opacity-50"
                >
                  {rotating ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                  Rotate token
                </button>
                <span className="text-[10px] text-neutral-600 font-body">Refreshes every 15 min</span>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default CalendarSyncButton;
